import {
  createContext,
  useCallback,
  useMemo,
  useState,
  type ReactElement,
  type ReactNode,
} from 'react';

/** Screen-space rect (viewport coordinates) captured for a shared-layout morph. */
export interface MorphRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export interface UiOverlayState {
  sidebarOpen: boolean;
  homeVisible: boolean;
  /**
   * Rect of the home prompt card at the moment of submit, used to morph the
   * brief/chat panel out of the exact spot the prompt lived. Consumed once by
   * the brief overlay, then cleared via `clearMorphOrigin`.
   */
  morphOrigin: MorphRect | null;
  openSidebar: () => void;
  closeSidebar: () => void;
  toggleSidebar: () => void;
  showHome: () => void;
  hideHome: (origin?: MorphRect | null) => void;
  clearMorphOrigin: () => void;
}

export const UiOverlayContext = createContext<UiOverlayState | null>(null);

export function UiOverlayProvider({ children }: { children: ReactNode }): ReactElement {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [homeVisible, setHomeVisible] = useState(true);
  const [morphOrigin, setMorphOrigin] = useState<MorphRect | null>(null);

  const openSidebar = useCallback(() => setSidebarOpen(true), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const toggleSidebar = useCallback(() => setSidebarOpen((prev) => !prev), []);
  const showHome = useCallback(() => {
    setMorphOrigin(null);
    setHomeVisible(true);
  }, []);
  const hideHome = useCallback((origin?: MorphRect | null) => {
    setMorphOrigin(origin ?? null);
    setHomeVisible(false);
  }, []);
  const clearMorphOrigin = useCallback(() => setMorphOrigin(null), []);

  const value = useMemo<UiOverlayState>(
    () => ({
      sidebarOpen,
      homeVisible,
      morphOrigin,
      openSidebar,
      closeSidebar,
      toggleSidebar,
      showHome,
      hideHome,
      clearMorphOrigin,
    }),
    [
      sidebarOpen,
      homeVisible,
      morphOrigin,
      openSidebar,
      closeSidebar,
      toggleSidebar,
      showHome,
      hideHome,
      clearMorphOrigin,
    ],
  );

  return <UiOverlayContext.Provider value={value}>{children}</UiOverlayContext.Provider>;
}
